"use client";

import { useEffect, useRef, useState } from "react";
import Script from "next/script";
import { isThirdPartyScriptSafe } from "@/lib/browserDetection";
import { useCookieConsent } from "./CookieConsentContext";
import { revokeVendorConsent, clearAnalyticsCookies } from "./analyticsTeardown";

const AnalyticsScripts = () => {
  const { hasConsent, isInitialized } = useCookieConsent();
  const [browserAllowed, setBrowserAllowed] = useState(false);
  const prevConsent = useRef<boolean | null>(null);

  // Derived in an effect rather than the render body: a `typeof window` branch
  // during render makes the server pass and the hydration pass disagree, and
  // suppressHydrationWarning on <body> does not cover descendants.
  useEffect(() => {
    const allowed = isThirdPartyScriptSafe();
    if (!allowed && process.env.NODE_ENV === "development") {
      console.warn("Analytics: Skipping - third-party scripts unsafe here");
    }
    setBrowserAllowed(allowed);
  }, []);

  // Withdrawal. next/script keeps a module-level LoadCache keyed on id and never
  // cleans up on unmount, so removing a <Script> neither unloads the executed JS
  // nor allows it to re-run on re-accept. Opt out where we can, clear what we can,
  // then hard reload — that is the only thing that actually stops Hotjar/Snap/GTM.
  useEffect(() => {
    if (!isInitialized) return;

    if (prevConsent.current === true && hasConsent !== true) {
      revokeVendorConsent();
      clearAnalyticsCookies();
      window.location.reload();
      return;
    }

    // Vendors fire unload beacons *after* the teardown above runs, which re-sets
    // some cookies on the way out. Sweep again on the next load whenever consent
    // is not granted; clearAnalyticsCookies is idempotent and cheap.
    if (hasConsent !== true) clearAnalyticsCookies();

    prevConsent.current = hasConsent;
  }, [hasConsent, isInitialized]);

  // hasConsent !== true (not !hasConsent) so that null — undecided — is excluded too.
  if (!isInitialized || hasConsent !== true || !browserAllowed) return null;

  return (
    <>
      {/* TikTok Pixel Code */}
      <Script
        id="tiktok-pixel"
        strategy="afterInteractive"
        onError={(e) => {
          console.warn("TikTok Pixel failed to load:", e);
        }}
      >
        {`
        !function (w, d, t) { 
          w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=[
            "page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie","holdConsent","revokeConsent","grantConsent"
          ],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(
          var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var r="https://analytics.tiktok.com/i18n/pixel/events.js",o=n&&n.partner;ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=r,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};n=document.createElement("script")
          ;n.type="text/javascript",n.async=!0,n.src=r+"?sdkid="+e+"&lib="+t;e=document.getElementsByTagName("script")[0];e.parentNode.insertBefore(n,e)};
          
          ttq.load('D0C6TL3C77U9CBHGRFMG'); 
          ttq.page(); 
        }(window, document, 'ttq');
        `}
      </Script>

      {/* Snap Pixel Code */}
      <Script
        id="snap-pixel"
        strategy="afterInteractive"
        onError={(e) => {
          console.warn("Snap Pixel failed to load:", e);
        }}
      >
        {`
        (function(e,t,n){if(e.snaptr)return;var a=e.snaptr=function() 
        {a.handleRequest?a.handleRequest.apply(a,arguments):a.queue.push(arguments)}; 
        a.queue=[];var s='script';r=t.createElement(s);r.async=!0; 
        r.src=n;var u=t.getElementsByTagName(s)[0]; 
        u.parentNode.insertBefore(r,u);})(window,document, 
        'https://sc-static.net/scevent.min.js'); 
        
        snaptr('init', 'a0091854-fbd1-4dd6-aa46-a6b2c50afe18', { 
          'user_email': '__INSERT_USER_EMAIL__' 
        }); 
        
        snaptr('track', 'PAGE_VIEW');
        `}
      </Script>

      <Script
        id="hotjar-script"
        strategy="afterInteractive"
        onError={(e) => {
          console.warn("Hotjar script failed to load:", e);
        }}
      >
        {`
          (function (c, s, q, u, a, r, e) {
        c.hj=c.hj||function(){(c.hj.q=c.hj.q||[]).push(arguments)};
        c._hjSettings = { hjid: a };
        r = s.getElementsByTagName('head')[0];
        e = s.createElement('script');
        e.async = true;
        e.src = q + c._hjSettings.hjid + u;
        r.appendChild(e);
    })(window, document, 'https://static.hj.contentsquare.net/c/csq-', '.js', ${process.env.NEXT_PUBLIC_CS_SITE_ID});
    `}
      </Script>

      {/* Meta Pixel Code */}
      <Script
        id="meta-pixel"
        strategy="afterInteractive"
        onError={(e) => {
          console.warn("Meta Pixel failed to load:", e);
        }}
      >
        {`
        !function(f,b,e,v,n,t,s) 
        {if(f.fbq)return;n=f.fbq=function(){n.callMethod? 
        n.callMethod.apply(n,arguments):n.queue.push(arguments)}; 
        if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0'; 
        n.queue=[];t=b.createElement(e);t.async=!0; 
        t.src=v;s=b.getElementsByTagName(e)[0]; 
        s.parentNode.insertBefore(t,s)}(window, document,'script', 
        'https://connect.facebook.net/en_US/fbevents.js'); 
        fbq('init', '1420250059133174'); 
        fbq('track', 'PageView');
        `}
      </Script>
      {/* Google Tag Manager  */}
       <Script
        id="google-tag-manager"
        strategy="afterInteractive"
      >{`(function(w,d,s,l,i){
        w[l]=w[l]||[];
        w[l].push({
          'gtm.start': new Date().getTime(),
          event:'gtm.js'
        });
        var f=d.getElementsByTagName(s)[0],
            j=d.createElement(s),
            dl=l!='dataLayer'?'&l='+l:'';
        j.async=true;
        j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;
        f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','GTM-KF7LC78Z');`}</Script> 
      {/*  End Google Tag Manager  */}

      {/* Google Analytics */}
       <Script
        src="https://www.googletagmanager.com/gtag/js?id=G-XKP0C4GSYN"
        strategy="lazyOnload"
      />
      <Script id="google-analytics" strategy="lazyOnload">
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());

          gtag('config', 'G-XKP0C4GSYN');
        `}
      </Script> 
    </>
  );
};

export default AnalyticsScripts;
